import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { Building2, Quote, Users, Mailbox, FileText, Image as ImageIcon } from "lucide-react";

export default function AdminDashboard() {
  const [counts, setCounts] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    adminApi.get("/admin/dashboard")
      .then(({ data }) => { if (mounted) setCounts(data.counts); })
      .catch(() => { if (mounted) setError("Failed to load dashboard."); });
    return () => { mounted = false; };
  }, []);

  const cards = [
    { label: "Subsidiaries", value: counts?.subsidiaries ?? "—", to: "/admin/subsidiaries", icon: Building2 },
    { label: "Testimonials", value: counts?.testimonials ?? "—", to: "/admin/testimonials", icon: Quote },
    { label: "Clients", value: counts?.clients ?? "—", to: "/admin/clients", icon: Users },
    { label: "New submissions", value: counts?.submissions_new ?? "—", to: "/admin/submissions", icon: Mailbox, highlight: true },
    { label: "Pages", value: counts?.pages ?? "—", to: "/admin/pages", icon: FileText },
    { label: "Total submissions", value: counts?.submissions_total ?? "—", to: "/admin/submissions", icon: Mailbox },
  ];

  return (
    <div data-testid="admin-dashboard-page">
      <div className="mb-10">
        <div className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium mb-2">Overview</div>
        <h1 className="font-serif text-navy text-3xl md:text-4xl">Dashboard</h1>
        <p className="text-mutedink text-sm mt-2">A quick view of your CMS. Click a tile to manage.</p>
      </div>
      {error && <div className="mb-6 text-red-700 bg-red-50 border border-red-200 p-3 text-sm">{error}</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map((c) => (
          <Link key={c.label} to={c.to}
                data-testid={`dashboard-card-${c.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`group bg-white border p-6 hover:border-gold transition-colors ${c.highlight ? "border-gold" : "border-softgray"}`}>
            <div className="flex items-center justify-between mb-4">
              <c.icon size={22} strokeWidth={1.5} className="text-gold" />
              <span className="text-[10px] uppercase tracking-wider text-mutedink">Manage →</span>
            </div>
            <div className="font-serif text-navy text-4xl mb-1">{c.value}</div>
            <div className="text-mutedink text-xs uppercase tracking-widest">{c.label}</div>
          </Link>
        ))}
      </div>

      <div className="mt-12 bg-white border border-softgray p-8">
        <h2 className="font-serif text-navy text-xl mb-4">Quick actions</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <Link to="/admin/testimonials" className="btn-outline-gold justify-center">Add testimonial</Link>
          <Link to="/admin/clients" className="btn-outline-gold justify-center">Add client</Link>
          <Link to="/admin/media" className="btn-outline-gold justify-center">Upload media</Link>
          <Link to="/admin/settings" className="btn-outline-gold justify-center">Edit site settings</Link>
        </div>
      </div>
    </div>
  );
}
